import Card from "../Card"
import SmallHeadingComponent from "../SmallHeadingComponent"
import {
    ActiveValueType,
    DataAvailabilityInformation,
    getSeverityColor,
    isActiveValueType,
    SeverityScore,
    TrendAnalysisResult,
    ValueType,
    valueTypes
} from "pokal_api"
import { ReactNode, useEffect, useState } from "react"
import { valueTypeNames } from "../../util/valueTypeNames"
import XMarkIcon from "../../icon/XMarkIcon"
import IconResizer from "../../icon/IconResizer"
import CheckBadgeIcon from "../../icon/CheckBadgeIcon"
import { useAppSelector } from "../../redux/store/hooks"
import {
    selectDashboardDataAvailabilities,
    selectDashboardDataSeverities,
    selectDashboardSelectedPatient,
    selectDashboardValueType
} from "../../redux/ProfessionalDashboard"
import notNull from "../../util/notNull"
import PokalColorConverter from "../../util/PokalColorConverter"
import trendAnalysisToString from "../../util/trendAnalysisToString"
import severityToString from "../../util/severityToString"
import Table from "../Table"
import Link from "../Link"
import {
    NAVIGATION_VIEW,
    NavigationPaths
} from "../../navigation/NavigationController"
import { getTrendAnalysisColor } from "pokal_api"

function ValueTypeListIcon(props: { children: ReactNode; prop: boolean }) {
    return (
        <IconResizer
            size={8}
            color={props.prop ? "text-green-600" : "text-rose-600"}>
            {props.children}
        </IconResizer>
    )
}

interface ValueTypeListProps {
    onSelect?: (valueType: ValueType) => void
}

function ValueTypeList(props: ValueTypeListProps) {
    const dataAvailability = useAppSelector(selectDashboardDataAvailabilities)
    const dataSeverities = useAppSelector(selectDashboardDataSeverities)

    const selectedPatient = useAppSelector(selectDashboardSelectedPatient)

    type DataSeveritiesAndAvailabilities = {
        type: ValueType
        info: DataAvailabilityInformation
        severity: {
            relative: TrendAnalysisResult | null
            absolute: SeverityScore | null
        }
    }
    const [data, setData] = useState<DataSeveritiesAndAvailabilities[] | null>(
        null
    )

    useEffect(() => {
        if (notNull(dataSeverities) && notNull(dataAvailability)) {
            const data = valueTypes.map((v) => ({
                type: v,
                info: dataAvailability[v],
                severity: {
                    relative: dataSeverities.relative[v],
                    absolute: isActiveValueType(v)
                        ? dataSeverities.absolute[v as ActiveValueType]
                        : null
                }
            }))
            setData(data)
        }
    }, [dataSeverities, dataAvailability])

    const dataSeverityHeader: string[] = [
        "Name",
        //"Klassifizierung",
        "Trend",
        "Aktuell",
        "Aktuelle Daten",
        "Baseline Daten",
        "Erster Eintrag",
        "Letzter Eintrag",
        "Anzahl"
    ]

    function getIcon(prop: boolean): ReactNode {
        return prop ? (
            <ValueTypeListIcon prop={prop}>
                <CheckBadgeIcon />
            </ValueTypeListIcon>
        ) : (
            <ValueTypeListIcon prop={prop}>
                <XMarkIcon />
            </ValueTypeListIcon>
        )
    }

    function getNodeFromDate(date: Date | null) {
        if (date !== null) return new Date(date).toLocaleDateString("de")
        return (
            <ValueTypeListIcon prop={false}>
                <XMarkIcon />
            </ValueTypeListIcon>
        )
    }

    function neutralBadge(msg: string) {
        return (
            <span className={PokalColorConverter.toBadge("neutral")}>
                {msg}
            </span>
        )
    }

    function severityInfoToReactNodes(entry: DataSeveritiesAndAvailabilities) {
        return [
            valueTypeNames[entry.type],
            //<DataBadge key={entry.type} content={entry.type} />,
            notNull(entry.severity.relative) ? (
                <span
                    className={PokalColorConverter.toBadge(
                        getTrendAnalysisColor(entry.severity.relative)
                    )}>
                    {trendAnalysisToString(entry.severity.relative)}
                </span>
            ) : (
                neutralBadge("Berechnung nicht möglich")
            ),
            !isActiveValueType(entry.type) ? (
                neutralBadge("Nur für aktive")
            ) : notNull(entry.severity.absolute) ? (
                <span
                    className={PokalColorConverter.toBadge(
                        getSeverityColor(
                            entry.severity.absolute as SeverityScore,
                            entry.type as ActiveValueType
                        )
                    )}>
                    {severityToString(
                        entry.severity.absolute as SeverityScore,
                        entry.type as ActiveValueType
                    )}
                </span>
            ) : (
                neutralBadge("Berechnung nicht möglich")
            ),
            getIcon(entry.info.currentData),
            getIcon(entry.info.baselineData),
            getNodeFromDate(entry.info.firstEntry),
            getNodeFromDate(entry.info.lastEntry),
            String(entry.info.numberOfEntries)
        ]
    }

    function onSelect(entry: DataSeveritiesAndAvailabilities) {
        selectDashboardValueType(valueTypes[entry.type])
        if (props.onSelect) props.onSelect(entry.type)
    }

    return (
        <Card fill={true} width={"100%"}>
            <div className={"flex flex-col space-y-4"}>
                <div className={"flex"}>
                    <SmallHeadingComponent>Daten-Auswahl</SmallHeadingComponent>
                    <div className={"ml-auto"}>
                        <Link
                            to={
                                NavigationPaths[
                                    NAVIGATION_VIEW
                                        .PROFESSIONAL_DASHBOARD_MEDICATION
                                ].path
                            }>
                            Zur Medikation
                        </Link>
                    </div>
                </div>
                {selectedPatient ? (
                    <>
                        <div>
                            für Patient:in {selectedPatient.firstName}{" "}
                            {selectedPatient.lastName}
                        </div>
                        <div>
                            {notNull(data) && (
                                <Table
                                    header={dataSeverityHeader}
                                    data={data.map((d) => ({
                                        ...d,
                                        id: d.type.toString()
                                    }))}
                                    rowBuilder={severityInfoToReactNodes}
                                    selectable={false}
                                    centered={true}
                                    onClick={(selection) => onSelect(selection)}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <div className={"italic"}>
                        Bitte wählen Sie eine Patient:in in der
                        Patient:innen-Auswahl aus.
                    </div>
                )}
            </div>
        </Card>
    )
}

export default ValueTypeList
